import { FC, useMemo } from "react";
import { Column } from "react-table";
import { SvgCaretUp } from "../../../../assets/icons/SvgCaretUp";
import { DropdownTable } from "../../../../common/components/Table/DropdownTable";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { useGetAdminWorkQuery } from "services/admin/adminReportsApi";
import {
  AdminWorkDepartment,
  IAdminWorkItem,
} from "common/models/admin/IReports";
import {
  formatHours,
  getQueryDateParam,
  timeFormatter,
} from "common/utils/helpers";

interface IAdminWorkRow {
  date?: string;
  label: string;
  count: number;
  resolved_count: number;
  total_hours: number;
  subRows?: IAdminWorkRow[];
}

// Дерево: день → отдел → сотрудник. Обращение относится к дню создания,
// «решено» и время — по отметке обработки (см. AdminWorkController на бэкенде)
function buildRows(
  items: IAdminWorkItem[],
  singleDepartment: boolean
): IAdminWorkRow[] {
  const sum = (rows: IAdminWorkRow[]) =>
    rows.reduce(
      (acc, row) => ({
        count: acc.count + row.count,
        resolved_count: acc.resolved_count + row.resolved_count,
        total_hours: acc.total_hours + row.total_hours,
      }),
      { count: 0, resolved_count: 0, total_hours: 0 }
    );

  const byDate = new Map<string, IAdminWorkItem[]>();
  items.forEach((item) => {
    byDate.set(item.date, [...(byDate.get(item.date) ?? []), item]);
  });

  return [...byDate.entries()].map(([date, dateItems]) => {
    const byDepartment = new Map<string, IAdminWorkItem[]>();
    dateItems.forEach((item) => {
      byDepartment.set(item.department.name, [
        ...(byDepartment.get(item.department.name) ?? []),
        item,
      ]);
    });

    const departmentRows: IAdminWorkRow[] = [...byDepartment.entries()].map(
      ([name, departmentItems]) => {
        const employeeRows = departmentItems.map((item) => ({
          label: item.employee.name,
          count: item.count,
          resolved_count: item.resolved_count,
          total_hours: item.total_hours,
        }));
        return { label: name, ...sum(employeeRows), subRows: employeeRows };
      }
    );

    // При одном отделе уровень отдела лишний: сразу сотрудники
    const children = singleDepartment
      ? departmentRows.flatMap((row) => row.subRows ?? [])
      : departmentRows;

    return {
      date,
      label: singleDepartment ? departmentRows[0]?.label ?? "" : "Все отделы",
      ...sum(children),
      subRows: children,
    };
  });
}

export const AdminWorkTable: FC<{
  department?: AdminWorkDepartment;
  dateFrom?: string;
  dateTo?: string;
}> = ({ department, dateFrom, dateTo }) => {
  const { data, isLoading, isFetching, error } = useGetAdminWorkQuery({
    department,
    date_from: getQueryDateParam(dateFrom),
    date_to: getQueryDateParam(dateTo),
  });

  const rows = useMemo(
    () => (data ? buildRows(data, !!department) : []),
    [data, department]
  );

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {rows.length ? (
        <DropdownTable data={rows} columns={columns} isLoading={isFetching} />
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const columns: Column<IAdminWorkRow>[] = [
  {
    Header: "Дата",
    accessor: "date",
    Cell: ({ row }: any) => (
      <span className="whitespace-nowrap">
        {row.original.date &&
          timeFormatter({
            time: new Date(row.original.date),
            mode: "dd-Mth-yyyy",
          })}
      </span>
    ),
  },
  {
    Header: "Отдел / сотрудник",
    accessor: "label",
    Cell: ({ row }: any) =>
      row.canExpand ? (
        <button
          className="flex h-full w-full items-center whitespace-nowrap text-left"
          {...row.getToggleRowExpandedProps({
            style: { paddingLeft: `${row.depth * 10}px` },
          })}
        >
          {row.original.label}
          {row.isExpanded ? (
            <SvgCaretUp className="ml-[5px] h-[7px] w-[7px]" />
          ) : (
            <SvgCaretUp className="ml-[5px] h-[7px] w-[7px] rotate-180" />
          )}
        </button>
      ) : (
        <span
          className="whitespace-nowrap"
          style={{ paddingLeft: `${row.depth * 10}px` }}
        >
          {row.original.label}
        </span>
      ),
  },
  {
    Header: "Обращений",
    accessor: "count",
  },
  {
    Header: "Решено",
    accessor: "resolved_count",
  },
  {
    Header: "Ср. время решения, ч",
    accessor: "total_hours",
    Cell: ({ row }: any) => (
      <>
        {row.original.resolved_count
          ? formatHours(row.original.total_hours / row.original.resolved_count)
          : "—"}
      </>
    ),
  },
];
